import {
    LayerType,
    TextWritingType,
    type BaseTextInfo,
    type ILayerProps,
    type ITextContent,
    type TextContentPadding,
} from '@/version-types';
import { extractBaseTextInfoFromTextLayer, extractBaseTextInfoFromVectorLayer } from './convert';
import { getListTextPreservePadding } from './list';
import { getListTextWithRichText } from './rich-text';
import { getVerticalLayoutStyles } from './style';

/**
 * 测量布局配置选项
 */
type MeasureLayoutOptions = {
    /**
     * 保持宽度不变
     */
    keepConstantWidth?: boolean;
    /**
     * 保持高度不变
     */
    keepConstantHeight?: boolean;
    /**
     * 预留 padding
     */
    preservePadding?: TextContentPadding;
    /**
     * 不允许换行
     * 默认值：false
     */
    noWrap?: boolean;
    /**
     * 是否取消使用最小尺寸
     * 默认值：false
     *
     * 由于矢量图层需要支持垂直布局，所以需要设置文本容器的最小尺寸
     * 但是在矢量图层 resize 操作时，则不需要使用最小尺寸，避免导致 resize 操作无法正常进行
     */
    cancelMinSize?: boolean;
};

/**
 * 文字测量类
 */
export class TextMeasurer {
    private constructor() {
        throw new Error('TextMeasurer is a static class');
    }

    private static readonly virtualContainerID = 'text-measurer-virtual-container';

    private static createNormalizedUlElement() {
        const $ul = document.createElement('ul');
        $ul.style.cssText = `
            margin: 0;
            padding: 0;
        `;

        return $ul;
    }

    private static createNormalizedLiElement() {
        const $li = document.createElement('li');
        $li.style.cssText = `
            margin: 0;
            padding: 0;
            position: relative;
        `;
        return $li;
    }

    private static createTextContentElement(textContent: ITextContent) {
        const $span = document.createElement('span');
        $span.style.cssText = `
            font-size: ${textContent.fontSize}px;
            font-family: ${textContent.fontFamily};
            font-weight: ${textContent.bold ? 'bold' : 'normal'};
            font-style: ${textContent.italic ? 'italic' : 'normal'};
            text-decoration: ${textContent.underline ? 'underline' : ''} ${textContent.lineThrough ? 'line-through' : ''};
            color: ${textContent.color};
        `;
        $span.innerText = textContent.text;
        return $span;
    }

    private static createListMockMarker(content: string | ITextContent[], baseTextInfo: BaseTextInfo) {
        const { writingMode, fontFamily, fontSize, bold, italic, listStyleMaterial } = baseTextInfo;
        const { paddingLeft, paddingTop } = getListTextPreservePadding(baseTextInfo);

        const $mock = document.createElement('div');
        $mock.setAttribute('data-mock-marker', 'container');
        $mock.style.cssText = `
            position: absolute;
            right: 0;
            bottom: 0;
            line-break: anywhere;
            visibility: hidden;
            pointer-events: none;
            font-size: ${fontSize}px;
            font-family: ${fontFamily};
            font-weight: ${bold ? 'bold' : 'normal'};
            font-style: ${italic ? 'italic' : 'normal'};
        `;

        if (writingMode === TextWritingType.HORIZONTAL_TB) {
            $mock.style.width = `calc(100% + ${paddingLeft}px)`;
            $mock.style.height = '100%';
        } else {
            $mock.style.width = '100%';
            $mock.style.height = `calc(100% + ${paddingTop}px)`;
        }

        const $marker = document.createElement('span');
        $marker.setAttribute('data-mock-marker', 'marker');
        $marker.innerText = 'x';
        $marker.style.display = 'inline-block';
        $marker.style.position = 'relative';

        if (writingMode === TextWritingType.HORIZONTAL_TB) {
            $marker.style.width = `${paddingLeft}px`;
        } else {
            $marker.style.height = `${paddingTop}px`;
        }

        if (listStyleMaterial) {
            const baseFontSize = listStyleMaterial.baseContentTextSize;
            const ratio = fontSize / baseFontSize;
            const width = listStyleMaterial.iconWidth * ratio * listStyleMaterial.scale;
            const height = listStyleMaterial.iconHeight * ratio * listStyleMaterial.scale;

            const $icon = document.createElement('i');
            $icon.setAttribute('data-mock-marker', 'icon');
            $icon.style.cssText = `
                width: ${width}px;
                height: ${height}px;
                position: absolute;
            `;

            if (writingMode === TextWritingType.HORIZONTAL_TB) {
                $icon.style.left = '0';
                $icon.style.top = '50%';
                $icon.style.transform = 'translateY(-50%)';
            } else {
                $icon.style.left = '50%';
                $icon.style.transform = 'translateX(-50%)';
            }

            $marker.appendChild($icon);
        }

        $mock.appendChild($marker);

        if (typeof content === 'string') {
            const $span = document.createElement('span');
            $span.textContent = content === '' ? '\u200b' : content;
            $span.setAttribute('data-mock-marker', 'content');
            $mock.appendChild($span);
        } else {
            const fragment = document.createDocumentFragment();
            content.forEach((item) => {
                const $span = this.createTextContentElement(item);
                $span.setAttribute('data-mock-marker', 'content');
                fragment.appendChild($span);
            });
            $mock.appendChild(fragment);
        }

        return $mock;
    }

    private static createInnerLayout(data: BaseTextInfo) {
        const { text, listStyle, writingMode, textContents = [] } = data;
        const segments = text.split('\n');

        // --- case 1: 非富文本 ---
        if (textContents.length === 0) {
            // --- case 1.1: 非列表文本 ---
            if (!listStyle) {
                return text.endsWith('\n') ? `${text}\n` : text;
            }

            // --- case 1.2: 列表文本 ---
            const $ul = this.createNormalizedUlElement();
            const { paddingLeft, paddingTop } = getListTextPreservePadding(data);

            if (writingMode === TextWritingType.HORIZONTAL_TB) {
                $ul.style.paddingLeft = `${paddingLeft}px`;
            } else {
                $ul.style.paddingTop = `${paddingTop}px`;
            }

            segments.forEach((item) => {
                const $li = this.createNormalizedLiElement();
                const node = document.createTextNode(item === '' ? '\u200b' : item);
                $li.appendChild(node);

                const $mock = this.createListMockMarker(item, data);
                $li.appendChild($mock);

                $ul.appendChild($li);
            });

            return $ul;
        }

        // --- case 2: 富文本 ---

        // --- case 2.1: 非列表文本 ---
        if (!listStyle) {
            const fragment = document.createDocumentFragment();
            textContents.forEach((item, idx) => {
                const $span = this.createTextContentElement(item);
                $span.setAttribute('data-origin-idx', String(idx));
                fragment.appendChild($span);
            });
            return fragment;
        }

        // --- case 2.2: 列表文本 ---
        const textContentGroup = getListTextWithRichText(textContents);
        const $ul = this.createNormalizedUlElement();
        const { paddingLeft, paddingTop } = getListTextPreservePadding(data);

        if (writingMode === TextWritingType.HORIZONTAL_TB) {
            $ul.style.paddingLeft = `${paddingLeft}px`;
        } else {
            $ul.style.paddingTop = `${paddingTop}px`;
        }

        textContentGroup.forEach((textContents) => {
            const $li = this.createNormalizedLiElement();
            const fragment = document.createDocumentFragment();

            if (textContents.length === 0) {
                fragment.appendChild(document.createTextNode('\u200b'));
            } else {
                textContents.forEach((item) => {
                    const $span = this.createTextContentElement(item);
                    $span.setAttribute('data-origin-idx', String(item.originIdx));
                    fragment.appendChild($span);
                });
            }

            $li.appendChild(fragment);

            const $mock = this.createListMockMarker(textContents, data);
            $li.appendChild($mock);

            $ul.appendChild($li);
        });

        return $ul;
    }

    public static measureListMarkerLayout(order: number, data: BaseTextInfo, options = { offset: 0 }) {
        const { writingMode, fontSize, listStyleMaterial } = data;
        const ret = { width: 0, height: 0, top: 0, left: 0, bottom: 0 };

        const $container = document.getElementById(this.virtualContainerID);
        if (!$container) return ret;

        const $ul = $container.querySelector('ul');
        if (!$ul || $ul.children.length === 0) return ret;

        const $li = $ul.children[order - 1] as HTMLLIElement;
        if (!$li) return ret;

        const $marker = $li.querySelector('[data-mock-marker="marker"]');
        if (!$marker) return ret;

        const range = document.createRange();
        range.selectNodeContents($marker);

        const { width, height, top, left, bottom } = range.getBoundingClientRect();
        const { offset } = options;

        if (writingMode === TextWritingType.HORIZONTAL_TB) {
            ret.width = parseInt($ul.style.paddingLeft, 10);
            ret.height = height;
            ret.left = offset ? offset : 0;
            ret.top = top;
        } else {
            ret.width = width;
            ret.height = parseInt($ul.style.paddingTop, 10);
            ret.left = left;
            ret.top = offset ? offset : 0;
        }

        if (listStyleMaterial) {
            const ratio = fontSize / listStyleMaterial.baseContentTextSize;
            ret.width = listStyleMaterial.iconWidth * ratio * listStyleMaterial.scale;
            ret.height = listStyleMaterial.iconHeight * ratio * listStyleMaterial.scale;

            const $icon = $marker.querySelector('[data-mock-marker="icon"]');
            if ($icon) {
                const { top, left } = $icon.getBoundingClientRect();

                if (writingMode === TextWritingType.HORIZONTAL_TB) {
                    ret.top = top;
                } else {
                    ret.left = left;
                }
            }
        }

        ret.bottom = bottom;

        return ret;
    }

    /**
     * 创建虚拟文本布局
     *
     * @param width - 当前容器宽度（通常指图层宽度）
     * @param height - 当前容器高度（通常指图层高度）
     * @param data - 文本信息
     * @param options - 可选：测量配置选项
     *     - `keepConstantWidth`：是否保持宽度不变
     *     - `keepConstantHeight`：是否保持高度不变
     *     - `preservePadding`：预留 padding
     */
    public static createVirtualLayoutInfo(layer: ILayerProps, options: MeasureLayoutOptions = {}) {
        const { width, height, layerType } = layer;

        let data: BaseTextInfo | null = null;

        if (layerType === LayerType.TEXT_LAYER) {
            data = extractBaseTextInfoFromTextLayer(layer);
        } else if (layerType === LayerType.VECTOR_LAYER) {
            data = extractBaseTextInfoFromVectorLayer(layer);
        }

        if (!data) {
            console.warn('TextMeasurer.measure: BaseTextInfo should not be null! -- ', layer);
            throw new Error('TextMeasurer.measure: BaseTextInfo should not be null! -- ' + JSON.stringify(layer));
        }

        const {
            fontFamily,
            fontSize,
            lineSpacing,
            letterSpacing,
            bold,
            italic,
            lineThrough,
            underline,
            textAlign,
            writingMode,
            verticalAlign,
        } = data;

        const {
            keepConstantWidth = false,
            keepConstantHeight = false,
            noWrap = false,
            cancelMinSize = false,
        } = options;
        const { preservePadding } = options;

        // --- container 样式设置 ---
        let containerWidth = 'auto';
        let containerHeight = 'auto';

        if (keepConstantHeight) {
            containerHeight = `${height}px`;
            containerWidth = 'max-content';
        } else {
            if (keepConstantWidth) {
                containerWidth = `${width}px`;
                containerHeight = 'auto';
            } else {
                containerWidth = 'auto';
                containerHeight = writingMode !== TextWritingType.HORIZONTAL_TB ? 'auto' : `${height}px`;
            }
        }

        let $container: HTMLDivElement | null = null;
        let $inner: HTMLDivElement | null = null;

        const $virtualContainer = document.getElementById(this.virtualContainerID);

        if (!$virtualContainer) {
            $container = document.createElement('div');
            $container.id = this.virtualContainerID;
            $inner = document.createElement('div');

            $container.appendChild($inner);
        } else {
            $container = $virtualContainer as HTMLDivElement;
            $inner = $container.firstChild as HTMLDivElement;
        }

        $container.style.cssText = `
            width: ${containerWidth};
            height: ${containerHeight};
            position: absolute;
            top: 0;
            left: 0;
            visibility: hidden;
            pointer-events: none;
        `;

        // --- inner 设置 ---
        $inner.style.cssText = `
            font-size: ${fontSize}px;
            font-family: ${fontFamily};
            margin: 0;
            padding-top: 0;
            padding-right: 0;
            padding-bottom: 0;
            padding-left: 0;
            white-space: ${noWrap ? 'nowrap' : 'break-spaces'};
            writing-mode: ${writingMode};
            line-height: ${lineSpacing < 0.01 ? 0.01 : lineSpacing};
            letter-spacing: ${letterSpacing}px;
            font-weight: ${bold ? 'bold' : 'normal'};
            text-align: ${textAlign};
            word-break: break-word;
            outline: none;
            line-break: auto;
            font-style: ${italic ? 'italic' : 'normal'};
            text-decoration: ${lineThrough ? 'line-through' : ''} ${underline ? 'underline' : ''};
            -webkit-text-size-adjust: none;
        `;

        if (keepConstantHeight) {
            $inner.style.height = '100%';
        }

        // 预留 padding 设置
        if (preservePadding) {
            const { top, right, bottom, left } = preservePadding;
            $inner.style.paddingTop = `${top}px`;
            $inner.style.paddingRight = `${right}px`;
            $inner.style.paddingBottom = `${bottom}px`;
            $inner.style.paddingLeft = `${left}px`;
        }

        // 垂直布局设置
        if (verticalAlign && !cancelMinSize) {
            const verticalStyle = getVerticalLayoutStyles(verticalAlign, writingMode);
            const minWidth = layer.__proactiveWidth || width;
            const minHeight = layer.__proactiveHeight || height;

            $inner.style.minHeight = `${minHeight}px`;
            if (writingMode !== TextWritingType.HORIZONTAL_TB) {
                $inner.style.minWidth = `${minWidth}px`;
            }

            $inner.style.display = verticalStyle.display!;
            $inner.style.flexDirection = verticalStyle.flexDirection!;
            $inner.style.justifyContent = verticalStyle.justifyContent!;
        }

        // // debug
        // $container.style.visibility = 'visible';
        // $container.style.zIndex = '9999999';
        // $container.style.backgroundColor = 'red';
        // $container.style.pointerEvents = 'auto';
        // $container.style.cursor = 'text';
        // $inner.style.userSelect = 'text';

        if (!$virtualContainer) {
            document.body.appendChild($container);
        }

        // --- 计算最小宽度 ---
        $inner.innerText = ' ';
        const minWidth = $inner.offsetWidth || fontSize;
        const minHeight = $inner.offsetHeight || fontSize * lineSpacing;

        // 设置内容
        const content = this.createInnerLayout(data);
        $inner.innerHTML = '';

        if (typeof content === 'string') {
            $inner.innerText = content;
        } else {
            $inner.appendChild(content);
        }

        const dispose = () => {
            $container?.remove();
        };

        return {
            minWidth,
            minHeight,
            $box: $inner,
            dispose,
        };
    }

    /**
     * 通过图层信息计算文本尺寸
     *
     * @param layer - 图层信息
     * @param options - 可选：测量配置选项
     * @returns - 文本尺寸信息
     */
    public static measure(layer: ILayerProps, options: MeasureLayoutOptions = {}) {
        const { width, height } = layer;
        const { keepConstantWidth, keepConstantHeight } = options;
        const ret = {
            width: 0,
            height: 0,
            minWidth: 0,
            minHeight: 0,
        };

        const virtualLayoutInfo = this.createVirtualLayoutInfo(layer, options);

        if (!virtualLayoutInfo) return ret;

        const { $box, minWidth, minHeight } = virtualLayoutInfo;

        const rect = $box.getBoundingClientRect();
        const boxWidth = rect.width || Math.abs(rect.right - rect.left);
        const boxHeight = rect.height || Math.abs(rect.top - rect.bottom);

        ret.width = keepConstantWidth ? width : Math.ceil(boxWidth || minWidth);
        ret.height = keepConstantHeight ? height : Math.ceil(boxHeight || minHeight);
        ret.minWidth = minWidth;
        ret.minHeight = minHeight;

        return ret;
    }
}
